import type { App } from 'vue';
import { decode } from 'js-base64';

// 复制 request.ts 中的 BASE_URL 逻辑，避免循环依赖
const ENV_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://www.ccfca.top/api/v1';
let baseUrl = ENV_BASE_URL;
// #ifdef H5
baseUrl = '/api/v1';
// #endif
const BASE_URL = baseUrl;

// 面包屑接口
export interface Breadcrumb {
  type: 'route' | 'click' | 'api' | 'custom' | 'error';
  category?: string;
  message?: string;
  data?: any;
  timestamp: number;
}

// 错误上报接口
export interface ErrorReport {
  project: string;
  error_type: string;
  error_msg: string;
  error_stack?: string;
  page_path?: string;
  device_info?: any;
  breadcrumbs?: Breadcrumb[];
  user_id?: number;
}

// 配置
const CONFIG = {
  maxBreadcrumbs: 20,
  reportUrl: '/monitor/report', // 相对路径
};

// 面包屑队列
const breadcrumbs: Breadcrumb[] = [];

/**
 * 添加面包屑
 */
export function addBreadcrumb(crumb: Omit<Breadcrumb, 'timestamp'>) {
  const item: Breadcrumb = {
    ...crumb,
    timestamp: Date.now(),
  };
  
  if (breadcrumbs.length >= CONFIG.maxBreadcrumbs) {
    breadcrumbs.shift();
  }
  breadcrumbs.push(item);
}

/**
 * 获取当前页面路径
 */
function getCurrentPagePath(): string {
  const pages = getCurrentPages();
  if (pages.length > 0) {
    const page = pages[pages.length - 1];
    return page ? page.route || '' : '';
  }
  return '';
}

/**
 * 上报错误
 */
export function reportError(error: Partial<ErrorReport>) {
  // 1. 获取设备信息
  const systemInfo = uni.getSystemInfoSync();
  
  // 2. 获取用户信息
  const token = uni.getStorageSync('customer_token');
  let userId: number | undefined;

  // 尝试从 Token 解析 User ID (JWT)
  if (token) {
      try {
          const parts = token.split('.');
          if (parts.length === 3) {
            // 使用 js-base64 解码 Payload
            const payloadStr = decode(parts[1]);
            const payload = JSON.parse(payloadStr);
            if (payload.sub) {
                userId = Number(payload.sub);
            }
          }
      } catch (e) {
          // ignore
      }
  }

  const reportData: ErrorReport = {
    project: 'mobile', // 默认为移动端
    error_type: error.error_type || 'js',
    error_msg: error.error_msg || 'Unknown Error',
    error_stack: error.error_stack,
    page_path: getCurrentPagePath(),
    device_info: {
      brand: systemInfo.brand,
      model: systemInfo.model,
      system: systemInfo.system,
      platform: systemInfo.platform,
      version: systemInfo.version, // 引擎版本
      appVersion: systemInfo.appVersion, // 应用版本
      ...error.device_info
    },
    breadcrumbs: [...breadcrumbs], // 复制当前面包屑
    user_id: userId,
    ...error,
  };

  // 拼接 URL
  let url = CONFIG.reportUrl;
  if (!url.startsWith('http')) {
    url = BASE_URL + url;
  }

  // 发送请求 (使用原生 uni.request 避免循环依赖和拦截器死循环)
  uni.request({
    url: url,
    method: 'POST',
    data: reportData,
    header: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    },
    success: () => {
      // console.log('Error reported successfully');
    },
    fail: (err) => {
      console.error('Monitor report failed:', err);
    }
  });
}

/**
 * 初始化监控
 */
export function setupMonitor(app: App) {
  // 1. Vue 错误捕获
  app.config.errorHandler = (err: any, vm, info) => {
    console.error('Vue Error:', err);
    reportError({
      error_type: 'vue',
      error_msg: err.message || String(err),
      error_stack: err.stack,
      device_info: { vue_info: info } 
    });
  };

  // 2. JS 运行时错误 (App/小程序)
  // #ifdef APP-PLUS || MP
  uni.onError((err) => {
    console.error('App Error:', err);
    reportError({
      error_type: 'js',
      error_msg: String(err),
    });
  });
  // #endif

  // 3. Web 端捕获
  // #ifdef H5
  if (typeof window !== 'undefined') {
    window.onerror = (msg, source, lineno, colno, error) => {
        reportError({
          error_type: 'js',
          error_msg: String(msg),
          error_stack: error?.stack,
        });
        return false;
      };
      window.addEventListener('unhandledrejection', (event) => {
        reportError({
          error_type: 'promise',
          error_msg: String(event.reason),
        });
      });
  }
  // #endif

  // 4. 路由拦截 (面包屑)
  const methods = ['navigateTo', 'redirectTo', 'switchTab', 'navigateBack', 'reLaunch'];
  methods.forEach(method => {
    uni.addInterceptor(method, {
      invoke(args) {
        addBreadcrumb({
          type: 'route',
          category: method,
          message: args.url || 'back', 
          data: args
        });
      }
    });
  });
}

export default {
  setupMonitor,
  addBreadcrumb,
  reportError
};
